// src/components/common/Navbar.jsx
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaBars,
  FaTimes,
  FaHome,
  FaProjectDiagram,
  FaCode,
  FaComments,
  FaTrophy,
  FaMicrophone,
  FaBriefcase,
  FaEnvelope,
  FaBook,
  FaBlog,
} from "react-icons/fa";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navItems = [
    { path: "/", label: "Home", icon: <FaHome /> },
    { path: "/projects", label: "Projects", icon: <FaProjectDiagram /> },
    { path: "/skills", label: "Skills", icon: <FaCode /> },
    { path: "/experience", label: "Experience", icon: <FaBriefcase /> },
    { path: "/testimonials", label: "Testimonials", icon: <FaComments /> },
    { path: "/achievements", label: "Achievements", icon: <FaTrophy /> },
    { path: "/talks", label: "Talks", icon: <FaMicrophone /> },
    { path: "/book", label: "Book", icon: <FaBook /> },
    { path: "/blog", label: "Blog", icon: <FaBlog /> },
    { path: "/contact", label: "Contact", icon: <FaEnvelope /> },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-md shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center h-16 gap-8">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Mihir Shah
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1 flex-1 justify-center">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-3 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 group ${
                  isActive(item.path)
                    ? "text-white"
                    : "text-gray-200 hover:text-white"
                }`}
              >
                {/* Active Background */}
                {isActive(item.path) && (
                  <motion.div
                    layoutId="navbar-active"
                    className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-lg border border-white/20"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}

                {/* Hover Background */}
                <div
                  className={`absolute inset-0 bg-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity ${
                    isActive(item.path) ? "hidden" : ""
                  }`}
                />

                <span className="relative text-sm font-medium drop-shadow-lg">
                  {item.icon}
                </span>
                <span className="relative text-sm font-medium drop-shadow-lg">
                  {item.label}
                </span>
              </Link>
            ))}
          </div>

          {/* VXDF Button */}
          <div className="hidden lg:block">
            <a
              href="https://vxdf.org"
              target="_blank"
              rel="noopener noreferrer"
              className="relative inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 text-white font-semibold text-sm rounded-xl border border-white/10 overflow-hidden group transition-all duration-500 hover:shadow-xl hover:shadow-purple-500/30 hover:scale-105 hover:border-white/20"
            >
              {/* Shimmer Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700" />

              <span className="relative z-10 font-bold tracking-wide">
                Explore VXDF
              </span>

              {/* Enhanced Icon */}
              <div className="relative z-10 flex items-center justify-center w-5 h-5 rounded-full bg-white/20 group-hover:bg-white/30 transition-all duration-300">
                <svg
                  className="w-3 h-3 transition-transform group-hover:translate-x-0.5 group-hover:scale-110"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                  />
                </svg>
              </div>

              {/* Hover Background */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 text-white hover:bg-white/20 transition-all duration-300"
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <FaTimes className="text-xl" />
            ) : (
              <FaBars className="text-xl" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-gray-900/95 backdrop-blur-md border-t border-white/10"
          >
            <div className="container mx-auto px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                    isActive(item.path)
                      ? "bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white border border-white/10"
                      : "text-gray-300 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}

              {/* Mobile VXDF Button */}
              <a
                href="https://vxdf.org"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25"
              >
                <span>Explore VXDF</span>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
